import { useCallback, useState } from "react";
import {
    NAME_STORAGE_KEY,
    ROLE_STORAGE_KEY,
    extractName,
    extractRole,
} from "./visitorPatterns";

const readLS = (key) => {
    try { return localStorage.getItem(key); }
    catch (err) { console.warn(`[visitor] read ${key} failed:`, err); return null; }
};

const writeLS = (key, val) => {
    try {
        if (val) localStorage.setItem(key, val);
        else localStorage.removeItem(key);
    } catch (err) {
        console.warn(`[visitor] write ${key} failed:`, err);
    }
};

/**
 * Manages the visitor's known first name and role for Jerry's banner.
 * Persists to localStorage so a returning visitor sees their name preserved.
 */
export default function useVisitorIdentity() {
    const [userName, setUserName] = useState(() => readLS(NAME_STORAGE_KEY) || null);
    const [userRole, setUserRole] = useState(() => readLS(ROLE_STORAGE_KEY) || null);

    /** Inspect a chat message; if it self-identifies, remember the name/role.
     *  Returns the (possibly updated) {name, role} that should be sent with the message. */
    const learnFromMessage = useCallback((msg) => {
        let nextName = userName;
        let nextRole = userRole;
        if (!userName) {
            const found = extractName(msg);
            if (found) {
                nextName = found;
                setUserName(found);
                writeLS(NAME_STORAGE_KEY, found);
            }
        }
        if (!userRole) {
            const role = extractRole(msg);
            if (role) {
                nextRole = role;
                setUserRole(role);
                writeLS(ROLE_STORAGE_KEY, role);
            }
        }
        return { name: nextName, role: nextRole };
    }, [userName, userRole]);

    const updateIdentity = useCallback((rawName, rawRole) => {
        const cleanName = (rawName || "").trim().slice(0, 40) || null;
        const cleanRole = rawRole || null;
        setUserName(cleanName);
        setUserRole(cleanRole);
        writeLS(NAME_STORAGE_KEY, cleanName);
        writeLS(ROLE_STORAGE_KEY, cleanRole);
    }, []);

    const forget = useCallback(() => {
        setUserName(null);
        setUserRole(null);
        writeLS(NAME_STORAGE_KEY, null);
        writeLS(ROLE_STORAGE_KEY, null);
    }, []);

    return { userName, userRole, learnFromMessage, updateIdentity, forget };
}
